/*  iceberg-ui  */
import {
    Button,
    Form,
    FormFieldGroup,
    FormInput,
    FormTextArea
} from '@auviknetworks/iceberg-ui';

/*  Shared  */
import { FormSelectHandler } from 'application/Shared/Components/Form/FormSelectHandler';
import { FormFactoryContextProvider } from 'application/Shared/Contexts/FormFactoryContext';
import { Notify } from 'application/Shared/Helpers';
import Helpers from 'application/Shared/Helpers/Functions';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';
import { Recipient } from 'hooks/dataAccess/Reports/scheduledReports';

/* Hooks */
import { useEmailReport } from 'hooks/dataAccess/Reports/useEmailReport';
import { useReportParams } from 'hooks/useReportParams';

/*  React  */
import React, { useState } from 'react';
import { UseFormSetError, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ReportDetails } from './ReportDetails';

interface EmailReportFormValues {
    recipients: string[];
    subject: string;
    description: string;
}

interface EmailReportModalProps {
    defaultSubject?: string;
    disabled?: boolean;
}

const EmailReportModalSendButton = ({
    isSubmitting
}: {
    isSubmitting: boolean;
}) => {
    const { t } = useTranslation();

    const { formState } = useFormContext();
    const { isValid } = formState;

    return (
        <Button
            data-trackid="emailReportModalSendButton"
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={isSubmitting || !isValid}
        >
            {t('Common.send')}
        </Button>
    );
};

/**
 * Modal component for configuring Email Report settings
 *
 * @param {EmailReportModalProps} props - The component props
 * @returns {JSX.Element} The EmailReportModal component
 */
export const EmailReportButtonWithModal = ({
    defaultSubject,
    disabled = false
}: EmailReportModalProps): JSX.Element => {
    const { t } = useTranslation();
    const { clientId, partnerId, reportType } = useParams();

    const parsedReportParams = useReportParams(reportType);

    const platformTenantId = clientId || partnerId;
    const [isOpen, setIsOpen] = useState(false);

    const { isSubmitting, sendEmailReport } =
        useEmailReport(parsedReportParams);

    const handleFormSubmit = async (
        data: EmailReportFormValues,
        setError: UseFormSetError<EmailReportFormValues>
    ) => {
        // Validate all recipient emails
        const invalidEmails = data.recipients.filter(
            (email) => !Helpers.isEmail(email)
        );
        if (invalidEmails.length > 0) {
            setError('recipients', {
                message: t('Reports.sendEmail.errorInvalidEmails'),
                type: 'validation'
            });
            return;
        }

        const recipients: Recipient[] = data.recipients.map((recipient) => ({
            type: 'email',
            value: recipient
        }));

        const success = await sendEmailReport({ ...data, recipients });

        if (success) {
            Notify.s(t('Reports.sendEmail.successMessage'));
            setIsOpen(false);
        } else {
            Notify.error(t('Reports.sendEmail.errorMessage'));
        }
    };

    return (
        <>
            <Button
                data-testid="reports_sendReportViaEmail_modal_open"
                data-trackid="reports_sendReportViaEmail_modal_open"
                onClick={() => setIsOpen(true)}
                disabled={disabled}
                variant="inverted"
                size="small"
            >
                {t('Reports.sendViaEmail')}
            </Button>

            <Modal
                disableEasyClose
                onClose={() => setIsOpen(false)}
                open={isOpen}
                paperProps={{ minWidth: '600px' }}
                title={t('Reports.sendViaEmail')}
                titleProps={{ showClose: true }}
            >
                <FormFactoryContextProvider
                    clientId={platformTenantId || ''}
                    applicationId=""
                >
                    <Form onSubmit={handleFormSubmit}>
                        <ModalBody>
                            <FormFieldGroup>
                                <FormSelectHandler
                                    name="recipients"
                                    label={t(
                                        'Reports.sendEmail.recipientsLabel'
                                    )}
                                    labelWidth={200}
                                    isMulti
                                    isClearable
                                    operation={{
                                        action: 'getUsers',
                                        serviceType: 'asm',
                                        valueAccessor: 'value'
                                    }}
                                    creatable
                                    registerProps={{
                                        required: t(
                                            'Reports.sendEmail.errorSelectRecipients'
                                        )
                                    }}
                                />
                                <FormInput
                                    name="subject"
                                    label={t('Reports.sendEmail.subjectLabel')}
                                    labelWidth={200}
                                    registerProps={{
                                        value: defaultSubject || ''
                                    }}
                                />
                                <FormTextArea
                                    name="description"
                                    label={t(
                                        'Reports.sendEmail.descriptionLabel'
                                    )}
                                    labelWidth={200}
                                    rows={3}
                                />
                            </FormFieldGroup>
                            <ReportDetails />
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                onClick={() => setIsOpen(false)}
                                variant="secondary"
                                type="button"
                            >
                                {t('Common.cancel')}
                            </Button>
                            <EmailReportModalSendButton
                                isSubmitting={isSubmitting}
                            />
                        </ModalFooter>
                    </Form>
                </FormFactoryContextProvider>
            </Modal>
        </>
    );
};
